import { Plus, Trash2 } from "lucide-react";
import {
  isGroup,
  type FieldDefinition,
  type FilterGroup,
  type FilterNode,
  type FilterProblem,
} from "@estelle/domain";

import { CombinatorToggle } from "@/components/filters/CombinatorToggle";
import { ConditionRow } from "@/components/filters/ConditionRow";
import { newCondition } from "@/components/filters/defaults";
import type { Path } from "@/components/filters/tree";
import { Button } from "@/components/ui/button";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { cn } from "@/lib/utils";

function problemAt(problems: FilterProblem[], path: Path): string | undefined {
  return problems.find(
    (p) =>
      p.path.length === path.length && p.path.every((v, i) => v === path[i]),
  )?.message;
}

/**
 * One combinator and its children, recursively.
 *
 * The AND/OR belongs to the group, not to a row: a per-row connector that
 * could differ from its neighbour is exactly the ambiguity ("a AND b OR c")
 * that grouping exists to remove. So the header switch sets it for the group,
 * and the connector beside each row after the first (`CombinatorToggle`) is
 * the same switch drawn where it is read — every one of them shows, and flips,
 * the one value.
 */
export function GroupEditor({
  group,
  path,
  fields,
  problems,
  depth = 0,
  onChangeNode,
  onRemove,
  onAppend,
}: {
  group: FilterGroup;
  path: Path;
  fields: FieldDefinition[];
  problems: FilterProblem[];
  depth?: number;
  onChangeNode: (path: Path, node: FilterNode) => void;
  onRemove: (path: Path) => void;
  onAppend: (path: Path, node: FilterNode) => void;
}) {
  const isRoot = path.length === 0;

  return (
    <div
      className={cn(
        "space-y-2",
        !isRoot && "bg-muted/40 rounded-md border p-2",
      )}
    >
      <div className="flex items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-md border">
          {(["and", "or"] as const).map((combinator) => (
            <button
              key={combinator}
              type="button"
              aria-pressed={group.combinator === combinator}
              onClick={() => {
                onChangeNode(path, { ...group, combinator });
              }}
              className={cn(
                "px-2 py-1 text-xs font-medium uppercase",
                group.combinator === combinator
                  ? SELECTED_CHOICE
                  : "text-muted-foreground hover:bg-accent",
              )}
            >
              {combinator}
            </button>
          ))}
        </div>
        <span className="text-muted-foreground text-xs">
          {group.combinator === "and"
            ? "match every condition"
            : "match any condition"}
        </span>

        {!isRoot ? (
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Remove group"
            className="ml-auto"
            onClick={() => {
              onRemove(path);
            }}
          >
            <Trash2 />
          </Button>
        ) : null}
      </div>

      <div className="space-y-2">
        {group.children.map((child, index) => {
          const childPath = [...path, index];

          return (
            <div key={index} className="flex items-start gap-1">
              {/*
                The group's AND/OR, written out in a gutter beside every row
                after the first (#492). The header switch says it once for the
                whole group; this is where a person reads "this and that" off
                the rows, and it flips the same switch. A gutter rather than a
                line of its own between the rows: one line each made a long
                filter taller than the screen. The first row has nothing
                there, so the rows stay in line.
              */}
              <div className="flex w-9 shrink-0 justify-center pt-1 pointer-coarse:pt-0">
                {index > 0 ? (
                  <CombinatorToggle
                    combinator={group.combinator}
                    className="pointer-coarse:min-h-11 pointer-coarse:min-w-9"
                    onToggle={() => {
                      onChangeNode(path, {
                        ...group,
                        combinator: group.combinator === "and" ? "or" : "and",
                      });
                    }}
                  />
                ) : null}
              </div>

              <div className="min-w-0 flex-1">
                {isGroup(child) ? (
                  <GroupEditor
                    group={child}
                    path={childPath}
                    fields={fields}
                    problems={problems}
                    depth={depth + 1}
                    onChangeNode={onChangeNode}
                    onRemove={onRemove}
                    onAppend={onAppend}
                  />
                ) : (
                  <ConditionRow
                    condition={child}
                    fields={fields}
                    problem={problemAt(problems, childPath)}
                    onChange={(next) => {
                      onChangeNode(childPath, next);
                    }}
                    onRemove={() => {
                      onRemove(childPath);
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}

        {group.children.length === 0 ? (
          <p className="text-muted-foreground px-1 text-xs">
            No conditions. This filter matches everything.
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="xs"
          variant="outline"
          onClick={() => {
            onAppend(path, newCondition(fields));
          }}
        >
          <Plus /> Condition
        </Button>
        {/*
          One level of nesting is enough for every question this UI has to
          express, and it is the level people reliably read back correctly.
        */}
        {depth < 1 ? (
          <Button
            size="xs"
            variant="ghost"
            onClick={() => {
              onAppend(path, {
                kind: "group",
                combinator: group.combinator === "and" ? "or" : "and",
                children: [newCondition(fields)],
              });
            }}
          >
            <Plus /> Group
          </Button>
        ) : null}
      </div>
    </div>
  );
}
